import {useContext, useMemo} from 'react';

import {apolloClient} from '../../../apolloClient';
import {AccountSelectorContext} from '../../../components/Search/SearchNav/AccountSelectorContextProvider';
import {
  AllQueuesLiteQueryResult,
  AllQueuesQueryResult,
  DefaultResourceConfig,
  DefaultResourceConfigEdge,
  EntityQueueByQueueNameLiteQueryResult,
  Project,
  PublicQueueQueryResult,
  QueueByIdQuery,
  RunQueueItemEdge,
  RunQueuePrioritizationMode,
  TemplateVariable,
} from '../../../generated/graphql';
import {RUNS_INFORMATION_QUERY} from '../../../state/graphql/launch';
import {
  addSeconds,
  isBefore,
  NUM_SECONDS_IN_MINUTE,
  subDaysLocal,
} from '../../../util/date';
import {runNamesFilter, toMongo} from '../../../util/filters';
import {useRampFlagAccountSelector} from '../../../util/rampFeatureFlags';
import {Account, AccountType} from '../../Search/SearchNav/types';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {caseInsensitiveCompare} from '../hooks';
import {JobDetails} from '../JobDetail/utilTypes';
import {WANDB_ENTITY_NAME} from '../publicQueue/constants';
import {isAllowedWandbPublicJob, isPublicQueue} from '../publicQueue/utils';
import {
  CREATE_QUEUE_PLACEHOLDER,
  GroupedQueueOptions,
  QueueOption,
} from '../SelectQueue2';
import {RunInfo, RunQueueItem} from '../types';
import {convertAgent} from '../utilData';
import {Agent, ExternalLink, ResourceType} from '../utilTypes';

export const RUN_QUEUE_ITEM_DISPLAY_THRESHOLD = 30;

// The default limit that a run should be queued before the agent kills it
const isNotRecent = (updatedAt: string | null): boolean => {
  if (updatedAt == null) {
    return false;
  }
  const threshold = addSeconds(new Date(), -30 * NUM_SECONDS_IN_MINUTE);
  const created = new Date(updatedAt);
  return isBefore(created, threshold);
};

// Map a run name to details about that run for display.
export type RunInfoMap = Record<string, RunInfo>;

export const extractResourceConfigs = (
  resourceConfigs: DefaultResourceConfigEdge[]
): Record<string, DefaultResourceConfig> => {
  const configs: Record<string, DefaultResourceConfig> = {};
  for (const edge of resourceConfigs) {
    configs[edge.node.id] = edge.node;
  }
  return configs;
};

export type QueueStatus = 'active' | 'inactive'; // Active means at least one agent is polling
const ACTIVE_AGENT_STATUSES = ['POLLING', 'RUNNING'];

export type QueueExternalLinks = {
  links: ExternalLink[];
};

export type Queue = {
  entityName: string;
  projectName: string;
  id: string;
  name: string;
  createdAt: string;
  status: QueueStatus;
  items: RunQueueItem[];
  prioritizationMode?: RunQueuePrioritizationMode;
  defaultResourceConfig: DefaultResourceConfig | null;
  variables?: TemplateVariable[];
  resourceType: ResourceType;
  externalLinks?: QueueExternalLinks;

  agents: Agent[];
};

// Helper to extract the queue and agent information from a project.
// This is pulled out so we can use it on both the user's actual projects
// and the special wandb jobs project.
const extractQueuesAndAgentsFromProject = (
  entityName: string,
  projectName: string,
  queueFilter: string | null,
  proj: Project,
  agents: Agent[],
  queues: Queue[]
) => {
  const projAgents = proj?.launchAgents ?? [];
  for (const projAgent of projAgents) {
    agents.push(convertAgent(projAgent));
  }

  const runQueues = proj.runQueue ? [proj.runQueue] : proj.runQueues;
  for (const q of runQueues) {
    if (queueFilter && q.name !== queueFilter) {
      continue;
    }

    const queueAgents = agents.filter(a => a.runQueues.includes(q.id));

    let status: QueueStatus = 'inactive';
    for (const a of queueAgents) {
      if (ACTIVE_AGENT_STATUSES.includes(a.agentStatus)) {
        status = 'active';
        break;
      }
    }

    const resourceType = q.defaultResourceConfig?.resource ?? 'unknown';
    const queue: Queue = {
      entityName,
      projectName,
      id: q.id,
      name: q.name,
      createdAt: q.createdAt.toString(),
      prioritizationMode: q.prioritizationMode,
      items: extractRunQueueItems(q.runQueueItems.edges as RunQueueItemEdge[]),
      defaultResourceConfig: q.defaultResourceConfig ?? null,
      variables: q.defaultResourceConfig
        ?.templateVariables as TemplateVariable[],
      resourceType: resourceType as ResourceType,
      externalLinks: q.externalLinks,
      status,
      agents: queueAgents,
    };
    queues.push(queue);
  }
};

const sortQueues = (queues: Queue[], personalEntity?: string) => {
  queues.sort((a, b) => {
    if (a.entityName === b.entityName) {
      return caseInsensitiveCompare(a.name, b.name);
    }
    // Sort wandb to top
    if (a.entityName === WANDB_ENTITY_NAME) {
      return -1;
    }
    if (b.entityName === WANDB_ENTITY_NAME) {
      return 1;
    }
    // Sort personal to bottom
    if (personalEntity != null && a.entityName === personalEntity) {
      return 1;
    }
    if (personalEntity != null && b.entityName === personalEntity) {
      return -1;
    }
    // Entity names are all lower case.
    return a.entityName.localeCompare(b.entityName);
  });
};

// Normalize query results, merging information about agents and queues
export const useExtractQueues = (
  queryAll: AllQueuesQueryResult,
  personalEntity: string
): Queue[] => {
  const agents: Agent[] = [];
  const queues: Queue[] = [];

  const viewerTeamEdges =
    queryAll.data?.viewer?.teams?.edges?.map(edge => edge.node) ?? [];
  const enableAccountSelector = useRampFlagAccountSelector();
  const {selectedAccount} = useContext(AccountSelectorContext);

  let filterByOrgId = null;
  let filterByEntityName = null;
  if (enableAccountSelector) {
    if (selectedAccount?.accountType === AccountType.Personal) {
      filterByEntityName = selectedAccount?.name;
    } else {
      filterByOrgId = selectedAccount?.id;
    }
  }

  for (const team of viewerTeamEdges) {
    if (!team || !team.launchProject) {
      continue;
    }

    // filter out non-selected orgs
    if (filterByOrgId && team.organizationId !== filterByOrgId) {
      if (team.organizationId == null && team.name === personalEntity) {
        // Always include the personal entity if a member of the team
      } else {
        continue;
      }
    }

    // filter out non personal entities (other than wandb)
    if (
      filterByEntityName &&
      ![filterByEntityName, WANDB_ENTITY_NAME].includes(team.name)
    ) {
      continue;
    }

    extractQueuesAndAgentsFromProject(
      team?.name,
      team.launchProject?.name, // always the default project
      null, // Don't filter queues
      team.launchProject as Project,
      agents,
      queues
    );
  }

  sortQueues(queues, personalEntity);
  return queues;
};

export const useExtractPublicQueues = (
  queryPublicQueues: PublicQueueQueryResult
) => {
  return useMemo(() => {
    const agents: Agent[] = [];
    const queues: Queue[] = [];
    const wandbEntity = queryPublicQueues.data?.entity;
    if (wandbEntity?.launchProject?.runQueue) {
      extractQueuesAndAgentsFromProject(
        wandbEntity.name,
        wandbEntity.launchProject.name,
        null, // Don't filter queues
        wandbEntity.launchProject as Project,
        agents,
        queues
      );
    }
    return queues;
  }, [queryPublicQueues.data]);
};

export const extractQueue = (
  entityName: string,
  data: QueueByIdQuery
): Queue | undefined => {
  if (!data.project?.runQueue) {
    return undefined;
  }

  const agents: Agent[] = [];
  const queues: Queue[] = [];

  extractQueuesAndAgentsFromProject(
    entityName,
    data.project.name,
    null, // don't filter queues
    data.project as Project,
    agents,
    queues
  );

  if (queues.length === 0) {
    return undefined;
  }

  return queues[0];
};

type JobFields = {
  // Entity and Project here refer to where the Job is defined. While that is the
  // default target entity/project for the created Run, the user can override.
  jobEntity: string;
  jobProject: string;
  versionedJob: string;
  jobName: string;
  alias: string;
};

// Parse job information (if available) into multiple fields to make it easier
// for downstream usage.
const extractJobFields = (job: string | undefined): JobFields => {
  const emptyJobFields = {
    jobEntity: '',
    jobProject: '',
    versionedJob: '',
    jobName: '',
    alias: '',
  };
  if (!job) {
    return emptyJobFields;
  }
  const [jobEntity, jobProject, versionedJob] = job.split('/');
  if (!jobEntity || !jobProject || !versionedJob) {
    return emptyJobFields;
  }
  const [jobName, alias] = versionedJob.split(':');
  return {
    jobEntity,
    jobProject,
    versionedJob,
    jobName,
    alias,
  };
};

export const extractRunQueueItems = (
  edges: RunQueueItemEdge[]
): RunQueueItem[] => {
  const items = [];
  for (const edge of edges) {
    const node = edge.node;
    const createdAt = node.createdAt;

    // TODO: Old run queue items are supposed to be cleared out by a glue task
    //       but that isn't happening yet. Instead, we filter them out in the frontend for now.
    //       https://wandb.atlassian.net/browse/WB-11510
    const threshold = subDaysLocal(
      new Date(),
      RUN_QUEUE_ITEM_DISPLAY_THRESHOLD
    );
    if (isBefore(new Date(createdAt), threshold) && node.state !== 'PENDING') {
      continue;
    }

    items.push({
      id: node.id,
      author: node.runSpec.author ?? null,
      entityName: node.runSpec.entity,
      projectName: node.runSpec.project,
      createdAt,
      updatedAt: node.createdAt.toString(),
      ...extractJobFields(node.runSpec.job),
      associatedRunId: node.associatedRunId ?? null,
      launchAgentId: node.launchAgentId ?? null,
      runSpec: node.runSpec,
      priority: node.priority,
      state: node.state,
      displayState: node.state, // Default, will override if we get run state info
      run: null,
      launchAgentName: null, // Default, will override if we get agent info
      templateVariableValues: node.templateVariableValues,
      error: node.error,
      warnings: node.warnings,
    });
  }
  return items;
};

// This method is used after we have queried for all of the details for associatedRunIds.
export const enhanceRunQueueItems = (
  items: RunQueueItem[],
  queue: Queue,
  runInfo: RunInfoMap
): RunQueueItem[] => {
  const enhanced = [];
  for (const item of items) {
    const {associatedRunId: runName, state: itemState} = item;
    item.launchAgentName =
      queue.agents?.find(a => a.id === item.launchAgentId)?.name ?? null;
    const run = runName && runInfo[runName] ? runInfo[runName] : null;

    // Update the display state based on run information
    // If none of the conditions apply, defaults to run queue item state.
    if (run) {
      item.displayState = run.state;
    } else if (itemState === 'PENDING' || itemState === 'LEASED') {
      item.displayState = queue.status === 'active' ? 'queued' : 'idle';
    } else if (itemState === 'CLAIMED') {
      if (isNotRecent(item.updatedAt)) {
        item.displayState = 'crashed';
      } else {
        item.displayState = queue.status === 'active' ? 'queued' : 'idle';
      }
    }
    enhanced.push({...item, run});
  }
  return enhanced;
};

// Iterate over a list of queues, building up a map from project
// to a list of associated run ids. Keys are in the format:
// <entity>/<project>
export const getProjectRunIdMap = (
  queues: Queue[]
): Record<string, string[]> => {
  const map: Record<string, string[]> = {};
  for (const queue of queues) {
    for (const item of queue.items) {
      if (!item.associatedRunId) {
        continue;
      }
      const key = `${item.entityName}/${item.projectName}`;
      if (!(key in map)) {
        map[key] = [];
      }
      map[key].push(item.associatedRunId);
    }
  }
  return map;
};

// Do one query per project name.
// Since we're looping over an unknown length list we can't use hooks.
// TODO: Have server expose a way of querying run id that isn't per-project.
export const getRunInformation = async (
  projectRunIdMap: Record<string, string[]>
) => {
  const runInfo: RunInfoMap = {};
  for (const [key, runIds] of Object.entries(projectRunIdMap)) {
    const [entityName, projectName] = key.split('/', 2);
    const filter = runNamesFilter(runIds);
    const filters = JSON.stringify(toMongo(filter));
    const response = await apolloClient.query({
      query: RUNS_INFORMATION_QUERY,
      variables: {
        entityName,
        projectName,
        filters,
      },
      fetchPolicy: 'no-cache',
    });
    if (!response.data.project) {
      // It is possible the run queue item references a project that was deleted.
      continue;
    } else if (!response.data.project.runs) {
      // Restricted projects without access will not have any runs.
      continue;
    }
    const runEdges = response.data.project.runs.edges ?? [];
    for (const runEdge of runEdges) {
      const {id, name, displayName, state, createdAt, heartbeatAt} =
        runEdge.node;
      runInfo[name] = {
        id,
        name,
        displayName,
        state,
        createdAt,
        heartbeatAt,
      };
    }
  }
  return runInfo;
};

export const isQueuePrioritizationEnabled = (
  queue: Pick<Queue, 'prioritizationMode'>
): boolean => {
  return queue.prioritizationMode === RunQueuePrioritizationMode.V0;
};

export const makeQueueOptionsFromQueueQueries = (
  allQueuesQuery: AllQueuesLiteQueryResult,
  filterInactive: boolean,
  jobIsPrivate: boolean,
  currentEntity: string,
  personalEntity: string,
  selectedAccount?: Account
): GroupedQueueOptions[] => {
  let entities =
    allQueuesQuery.data?.viewer?.teams?.edges.map(x => x.node) ?? [];

  let filterByOrgId = null;
  let filterByEntityName = null;
  if (selectedAccount) {
    if (selectedAccount?.accountType === AccountType.Personal) {
      filterByEntityName = selectedAccount?.name;
    } else {
      filterByOrgId = selectedAccount?.id;
    }
  }

  if (jobIsPrivate) {
    // filter out non-entity queues
    entities = entities.filter(entity => entity?.name === currentEntity);
  }

  const queueOptions: GroupedQueueOptions[] = [];
  let group = null;
  let groupOptions: QueueOption[] = [];
  for (const entity of entities) {
    if (!entity || !entity.launchProject || !entity.launchProject.runQueues) {
      continue;
    }

    // filter out non-selected orgs
    if (filterByOrgId && entity.organizationId !== filterByOrgId) {
      if (entity.organizationId == null && entity.name === personalEntity) {
        // Always include the personal entity if a member of the team
      } else {
        continue;
      }
    }

    // filter out non personal entities (other than wandb)
    if (
      filterByEntityName &&
      ![filterByEntityName, WANDB_ENTITY_NAME].includes(entity.name)
    ) {
      continue;
    }

    for (const queue of entity.launchProject.runQueues ?? []) {
      if (entity.name !== group) {
        group = entity.name;
        groupOptions = [];
        queueOptions.push({
          label: entity.name,
          options: groupOptions,
        });
      }

      let status = 'inactive';
      for (const agent of entity.launchProject.launchAgents ?? []) {
        if (!agent.runQueues.includes(queue.id)) {
          continue;
        }
        if (ACTIVE_AGENT_STATUSES.includes(agent.agentStatus)) {
          status = 'active';
        }
      }

      if (status === 'inactive' && filterInactive) {
        continue;
      }

      const queueOption: QueueOption = {
        entity: entity.name,
        project: entity.launchProject.name,
        isActive: status === 'active',
        value: `${entity.name}/${queue.name}`,
        queue: queue.name,
        queueID: queue.id,
        prioritizationMode:
          queue.prioritizationMode ?? RunQueuePrioritizationMode.Disabled,
        resourceType: queue.defaultResourceConfig?.resource as ResourceType,
        config:
          queue.defaultResourceConfig?.config?.resource_args[
            queue.defaultResourceConfig?.resource
          ] ?? {},
        defaultResourceConfigID: queue.defaultResourceConfig?.id ?? '',
        variables: queue.defaultResourceConfig?.templateVariables ?? [],
        disabled: false,
        additionalServices:
          queue.defaultResourceConfig?.config?.additionalServices ?? [],
        fullConfig: queue.defaultResourceConfig?.config?.resource_args ?? {},
      };

      groupOptions.push(queueOption);
    }

    if (
      queueOptions.length === 0 &&
      !(allQueuesQuery.loading || allQueuesQuery.error)
    ) {
      queueOptions.push({
        label: personalEntity,
        options: [CREATE_QUEUE_PLACEHOLDER],
      });
    }
  }

  return queueOptions;
};

export const makeQueueOptionsFromPublicQueueQuery = (
  publicQueuesQuery: EntityQueueByQueueNameLiteQueryResult,
  filterInactive: boolean
): GroupedQueueOptions[] => {
  const wandbEntity = publicQueuesQuery.data?.entity;
  if (
    !wandbEntity ||
    !wandbEntity.launchProject ||
    !wandbEntity.launchProject.runQueue
  ) {
    return [];
  }

  const queueOptions: GroupedQueueOptions[] = [];
  const group = wandbEntity.name;
  const groupOptions: QueueOption[] = [];

  queueOptions.push({
    label: group,
    options: groupOptions,
  });

  let status = 'inactive';
  const queue = wandbEntity.launchProject.runQueue;
  for (const agent of wandbEntity.launchProject.launchAgents ?? []) {
    if (!agent.runQueues.includes(wandbEntity.launchProject.runQueue.id)) {
      continue;
    }
    if (ACTIVE_AGENT_STATUSES.includes(agent.agentStatus)) {
      status = 'active';
    }
  }

  if (status === 'inactive' && filterInactive) {
    return [];
  }

  const queueOption: QueueOption = {
    entity: wandbEntity.name,
    project: wandbEntity.launchProject.name,
    isActive: status === 'active',
    value: `${wandbEntity.name}/${queue.name}`,
    queue: queue.name,
    queueID: queue.id,
    prioritizationMode:
      queue.prioritizationMode ?? RunQueuePrioritizationMode.Disabled,
    resourceType: queue.defaultResourceConfig?.resource as ResourceType,
    config:
      queue.defaultResourceConfig?.config?.resource_args[
        queue.defaultResourceConfig?.resource
      ] ?? {},
    defaultResourceConfigID: queue.defaultResourceConfig?.id ?? '',
    variables: queue.defaultResourceConfig?.templateVariables ?? [],
    disabled: false,
    additionalServices:
      queue.defaultResourceConfig?.config?.additionalServices ?? [],
    fullConfig: queue.defaultResourceConfig?.config?.resource_args ?? {},
  };

  groupOptions.push(queueOption);

  return queueOptions;
};

export const filterQueuesBasedOnJobSelection = (
  queues: GroupedQueueOptions[],
  job: JobDetails
) => {
  // If job is allowed on public queue, no filtering needed
  if (isAllowedWandbPublicJob(job.name, job.projectName, job.entityName)) {
    return queues;
  }

  // Filter out public queues
  return queues.map(queueGroup => {
    if (queueGroup.label !== WANDB_ENTITY_NAME) {
      return queueGroup;
    }

    return {
      label: queueGroup.label,
      options: queueGroup.options.filter(
        q => !isPublicQueue(q.queue, q.entity)
      ),
    };
  });
};

// Helper function that mimics the display state calculation logic from enhanceRunQueueItems.
export const isRecentlyEnqueued = (item: RunQueueItem) => {
  const {state: itemState, run} = item;

  // If there's a run object, it's not recently enqueued
  if (run) {
    return false;
  }

  if (itemState === 'PENDING' || itemState === 'LEASED') {
    return true;
  } else if (itemState === 'CLAIMED') {
    // todo(dom): we need more visibility into the status of claimed, but not started runs for this.
    if (isNotRecent(item.updatedAt)) {
      return false;
    } else {
      return true;
    }
  }

  return false;
};
